import { DefinitionActionKindEnum } from '@acme/api';
import type { ActionResultData } from '@acme/features';
import { findAction } from '@acme/features';
import { $application } from '@entities/application';
import {
  actionSucceeded,
  confirmRequested,
  deleteRequested,
} from '@features/applicationActions';
import { createEvent, createStore, sample } from 'effector';
import { getApplicationStatusAlert } from '../../../pages/application/lib/applicationStatusAlert';

export const confirmClicked = createEvent();
export const deleteClicked = createEvent();
export const actionResultReset = createEvent();

export type ActionResult = {
  isError: boolean;
  actionName?: string | null;
  customResult?: ActionResultData;
};

export const actionResultShown = createEvent<ActionResult>();

export const $actionResult = createStore<ActionResult | null>(null)
  .on(actionResultShown, (_, result) => result)
  .on(actionResultReset, () => null);

export const $actions = $application.map((app) => app?.actions ?? null);
export const $statusAlert = $application.map((app) =>
  getApplicationStatusAlert(app?.epoch ?? null)
);

const $pendingActionName = createStore<string | null>(null);

sample({
  clock: confirmClicked,
  source: $actions,
  filter: (actions) =>
    findAction(actions, DefinitionActionKindEnum.Confirm)?.id != null,
  fn: (actions) =>
    findAction(actions, DefinitionActionKindEnum.Confirm)?.name ?? null,
  target: $pendingActionName,
});

sample({
  clock: confirmClicked,
  source: $actions,
  filter: (actions) =>
    findAction(actions, DefinitionActionKindEnum.Confirm)?.id != null,
  fn: (actions) => ({
    actionId: findAction(actions, DefinitionActionKindEnum.Confirm)!.id!,
  }),
  target: confirmRequested,
});

sample({
  clock: deleteClicked,
  source: $actions,
  filter: (actions) =>
    findAction(actions, DefinitionActionKindEnum.Delete)?.id != null,
  fn: (actions) =>
    findAction(actions, DefinitionActionKindEnum.Delete)?.name ?? null,
  target: $pendingActionName,
});

sample({
  clock: deleteClicked,
  source: $actions,
  filter: (actions) =>
    findAction(actions, DefinitionActionKindEnum.Delete)?.id != null,
  fn: (actions) => ({
    actionId: findAction(actions, DefinitionActionKindEnum.Delete)!.id!,
  }),
  target: deleteRequested,
});

sample({
  clock: actionSucceeded,
  source: { application: $application, pendingActionName: $pendingActionName },
  fn: ({ pendingActionName }) => ({
    isError: false,
    actionName: pendingActionName,
  }),
  target: actionResultShown,
});
